/**
 * Per-decision timer ring during PLAYER_ACTIONS, driven by the server
 * `deadlineTs`. Display only — the server's `setAlarm` enforces the timeout
 * (= auto-STAND, GAME_SPEC §3). Resets visually whenever the deadline changes
 * (the server pushes a new deadline on each hit).
 */

import { useRef } from 'react';
import { useGameStore } from '../state/store';
import { useCountdown } from './useCountdown';

export function DecisionTimer() {
  const phase = useGameStore((s) => s.server.phase);
  const deadlineTs = useGameStore((s) => s.server.deadlineTs);
  const { seconds, fractional, active } = useCountdown(deadlineTs);

  // Denominator for the depleting ring: the FULL window this deadline started
  // with, captured the moment the deadline (re)appears. Deriving it from the
  // deadline itself — rather than `config.turnTimerSecs`, which can lag/mismatch
  // the actual window and pinned `frac` near 1 so the ring never emptied — makes
  // the ring always start full and burn down to empty.
  const totalRef = useRef(0);
  const lastDeadline = useRef<number | undefined>(undefined);
  if (deadlineTs !== lastDeadline.current) {
    lastDeadline.current = deadlineTs;
    totalRef.current = fractional > 0 ? fractional : 0;
  }

  if (phase !== 'PLAYER_ACTIONS' || !active) return null;

  const total = totalRef.current || 1;
  const frac = Math.max(0, Math.min(1, fractional / total));
  const R = 26;
  const C = 2 * Math.PI * R;
  const urgent = seconds <= 3;

  return (
    <div className={`decision-timer ${urgent ? 'is-urgent' : ''}`}>
      <svg width="64" height="64" viewBox="0 0 64 64">
        <circle cx="32" cy="32" r={R} className="decision-timer__track" />
        {/* Dash values go through inline STYLE (CSS properties React updates each
            frame), NOT presentation attributes — a CSS `transition` on
            stroke-dashoffset would otherwise swallow attribute updates and pin
            the ring full. No transition: the rAF re-render is already smooth. */}
        <circle
          cx="32"
          cy="32"
          r={R}
          className="decision-timer__fill"
          transform="rotate(-90 32 32)"
          style={{ strokeDasharray: C, strokeDashoffset: C * (1 - frac) }}
        />
      </svg>
      <span className="decision-timer__num numeric">{seconds}</span>
    </div>
  );
}
